import React, { useMemo } from 'react';
import {
  getExtraPermissionOptions,
  getPermissionLabel,
  getRoleLabel,
  getRolePermissions,
} from '../../utils/permissions';

const RoleAccessSummary = ({ role, extraPermissions = [], onExtraPermissionsChange, editable = false }) => {
  const rolePermissions = useMemo(() => getRolePermissions(role), [role]);
  const extraOptions = useMemo(() => getExtraPermissionOptions(role), [role]);

  const toggleExtra = (permission) => {
    if (!editable || !onExtraPermissionsChange) return;
    const next = extraPermissions.includes(permission)
      ? extraPermissions.filter((key) => key !== permission)
      : [...extraPermissions, permission];
    onExtraPermissionsChange(next);
  };

  if (!role) {
    return (
      <p className="text-sm text-muted-foreground">
        Select a role to preview default module access.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-foreground">{getRoleLabel(role)}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {rolePermissions.length} default function{rolePermissions.length === 1 ? '' : 's'}
        </p>
        <ul className="mt-2 max-h-36 overflow-y-auto space-y-1 text-sm text-muted-foreground">
          {rolePermissions.map((permission) => (
            <li key={permission} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              {getPermissionLabel(permission)}
            </li>
          ))}
        </ul>
      </div>

      {editable && extraOptions.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground">Additional access (optional)</p>
          <p className="text-xs text-muted-foreground mt-1">
            Grant extra modules beyond the role template.
          </p>
          <div className="mt-2 max-h-40 overflow-y-auto space-y-2 border border-border rounded-md p-3">
            {extraOptions.map(({ value, label }) => (
              <label key={value} className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={extraPermissions.includes(value)}
                  onChange={() => toggleExtra(value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {!editable && extraPermissions?.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground">Extra permissions</p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {extraPermissions.map((permission) => (
              <li key={permission}>{getPermissionLabel(permission)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RoleAccessSummary;
